import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { chatApi } from 'api/chat';
import { Spinner } from 'components/_new/Spinner';
import { Avatar } from 'components/_new/Avatar';
import { userDisplayName } from 'utils/userDisplayName';
import { Divider } from 'components/_new/Divider';
import { ProgramMembership } from 'types/Membership';
import { Button } from 'components/_new/Button';
import { useChat } from './useChat';
import { ChatConversationWindow } from './ChatConversationWindow';
import { useChatContext } from './ChatContextProvider';

type ChatConversationProps = {
	threadId: number;
	onThreadLoad?: (interlocutorProgramMembership: ProgramMembership) => void;
	onBackClick?: () => void;
};

export const ChatConversation = (props: ChatConversationProps) => {
	const { threadId, onThreadLoad, onBackClick } = props;
	const { t } = useTranslation();

	const { currentProgramMembershipId } = useChatContext();
	const { queryUniqueParam } = useChat();

	const {
		data: threadData,
		isLoading: threadLoading,
		isSuccess: threadSuccess,
		isError: threadError,
	} = useQuery(
		['chat-thread', { threadId, ...queryUniqueParam }],
		() => chatApi.getThread(currentProgramMembershipId!, threadId),
		{
			enabled: Boolean(currentProgramMembershipId),
			onSuccess: ({ interlocutorProgramMembership }) => {
				onThreadLoad?.(interlocutorProgramMembership);
			},
		}
	);

	return threadLoading ? (
		<Spinner />
	) : (
		<>
			{threadError && <div>{t('chat.canNotWriteToThisUser')}</div>}
			{threadData && threadSuccess && (
				<div className="flex-1 flex flex-column gap-2 h-full min-w-0 overflow-y-hidden overflow-x-hidden">
					<div className="flex-0 flex flex-row gap-2 md:gap-3 w-full px-2 pt-1 align-items-center">
						{onBackClick && (
							<Button
								label={t('actions.back')}
								icon="arrow-left"
								iconOnly
								variant="primary-text"
								variantSize="lg"
								circled
								className="flex md:hidden flex-shrink-0"
								style={{ width: 40, height: 40 }}
								onClick={onBackClick}
							/>
						)}
						<Avatar
							id={threadData.interlocutorProgramMembership.user.id}
							name={userDisplayName(threadData.interlocutorProgramMembership.user, null)}
							src={threadData.interlocutorProgramMembership.user.avatar}
							size="lg"
							className="flex-shrink-0"
						/>
						<div className="flex flex-column gap-0 min-w-0 flex-1 overflow-hidden">
							<span
								className="text-base md:text-lg text-primary font-bold overflow-hidden white-space-nowrap text-overflow-ellipsis"
								title={userDisplayName(threadData.interlocutorProgramMembership.user)}
							>
								{userDisplayName(threadData.interlocutorProgramMembership.user)}
							</span>
							<span
								className="text-xs text-gray overflow-hidden white-space-nowrap text-overflow-ellipsis"
								title={threadData.interlocutorProgramMembership.user.email}
							>
								{threadData.interlocutorProgramMembership.user.email}
							</span>
						</div>
					</div>
					<Divider margin={0} />
					<ChatConversationWindow thread={threadData} />
				</div>
			)}
		</>
	);
};
